<script setup lang="ts">
import Galleria from "primevue/galleria";
import { computed, inject, ref, toRefs } from "vue";
import { CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION, DEFAULT_EXCURSION_IMAGE } from "@/constants.ts";
import { imageTransformation } from "@/util/cloudinary.js";
import { getRidOfJSON } from "@/util/removeJson.ts";
import { ExcursionImageLoadingKey } from "../ExcursionConfigurator/types.ts";
import { excursionImagesPresets } from "./styles.ts";
import type { Props } from "./types.ts";

interface ImageDimension {
  width: number;
  height: number;
}

defineOptions({
  name: "ExcursionImages",
});

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  imageAlt: "",
  resolution: "low",
});

const { modelValue } = toRefs(props);

const DEFAULT_WIDTH: number = 377;
const DEFAULT_HEIGHT: number = 212;
const desiredImageRatio: number = DEFAULT_WIDTH / DEFAULT_HEIGHT;
const FETCH_DPR_MULTIPLIER: number = 3;

const scaler = computed<number>(() => {
  switch (props.resolution) {
    case "low":
      return 1;
    case "medium":
      return 1.25;
    case "high":
      return 1.5;
  }
});

const scaledWidth = computed<number>(() => Math.round(DEFAULT_WIDTH * scaler.value));
const scaledHeight = computed<number>(() => Math.round(DEFAULT_HEIGHT * scaler.value));

const imageDimensions = computed<ImageDimension>(() => {
  return {
    width: Math.round(scaledWidth.value * FETCH_DPR_MULTIPLIER),
    height: Math.round(scaledHeight.value * FETCH_DPR_MULTIPLIER),
  };
});

const galleriaPreset = excursionImagesPresets.galleria;

const isHovering = ref(false);

const excursionImages = computed<string[]>(() => {
  const imagePaths: string[] = modelValue.value ? getRidOfJSON(modelValue.value) : [];
  // Fall back to the default image whenever the event has no usable image data (a falsy
  // OR a truthy-but-empty `event.image`, e.g. "[]"). Without this the Galleria renders no
  // <img>, its @load/@error never fires, and the configurator skeleton waits on it forever.
  const safeImagePaths: string[] = imagePaths.length > 0 ? imagePaths : [DEFAULT_EXCURSION_IMAGE];
  return safeImagePaths.map((path: string) =>
    imageTransformation({
      ...CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION,
      compress: true,
      ...imageDimensions.value,
      path: path,
    }),
  );
});

const enableHovering = (hovering: boolean): void => {
  if (excursionImages.value.length <= 1) {
    return;
  }
  isHovering.value = hovering;
};

// The dots are the only control over the photo: paging a multi-image excursion is those, the hover
// autoplay, and the Galleria's touch swipe. Arrows here would compete with the carousel's own.
const showIndicators = computed(() => excursionImages.value.length > 1);

const imageLoading = inject(ExcursionImageLoadingKey, null);
const hasMarkedLoaded = ref(false);

const handleImageSettled = (): void => {
  if (hasMarkedLoaded.value) return;
  hasMarkedLoaded.value = true;
  imageLoading?.markImageGroupLoaded();
};
</script>

<template>
  <div
    @mouseenter="enableHovering(true)"
    @mouseleave="enableHovering(false)"
    class="flex w-full"
    data-testid="excursion-images"
  >
    <!-- Height needs no reservation of its own: the item's aspect-ratio box derives it from the
         rendered width, so the box holds its space before the image loads. A px width or height
         here would instead pin the image at that size and overflow narrower containers.

         Keep this a single root element — `data-excursion-card-media` and
         `data-experience-card-media` are set by callers and reach the DOM through attribute
         fallthrough, which a fragment root would silently drop. -->
    <Galleria
      :value="excursionImages"
      :numVisible="5"
      :showThumbnails="false"
      :showIndicators="showIndicators"
      :showIndicatorsOnItem="true"
      :circular="true"
      :autoPlay="isHovering"
      :transitionInterval="2500"
      :indicatorsPosition="'bottom'"
      :pt="galleriaPreset"
    >
      <template #item="{ item }">
        <div
          class="relative w-full"
          :style="{ aspectRatio: desiredImageRatio }"
        >
          <img
            :src="item"
            :alt="imageAlt"
            class="absolute inset-0 w-full h-full object-cover"
            data-testid="excursion-image"
            @load="handleImageSettled"
            @error="handleImageSettled"
          />
        </div>
      </template>
    </Galleria>
  </div>
</template>
